import { supabase } from '@/lib/supabase';
import { ServiceResult, createError } from './types';
import type { AnalysisStatus, RoomHostVideo, VideoAnalysis } from '@/types';

const DEFAULT_SIGNED_URL_EXPIRY_SECONDS = 3600;
const VIDEO_BUCKET = 'cleaning-videos';
const HOST_VIDEO_BUCKET = 'host-videos';

export interface VideoMetadata {
  cleaningId: string;
  roomId: string;
  durationSeconds: number;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export interface CleaningVideoRecord {
  id: string;
  cleaning_id: string;
  room_id: string;
  video_url: string;
  duration_seconds: number | null;
  size_bytes: number | null;
  recorded_at: string;
  analysis_status: AnalysisStatus;
}

export interface UploadVideoResult {
  signedUrl: string;
  cleaningVideoId: string;
  analysisTriggered: boolean;
}

export async function uploadVideo(
  metadata: VideoMetadata,
  blob: Blob,
  onProgress?: (progress: UploadProgress) => void
): Promise<ServiceResult<UploadVideoResult>> {
  const { cleaningId, roomId, durationSeconds } = metadata;
  const timestamp = Date.now();
  
  // Use correct extension based on blob type or filename
  let extension = 'webm';
  const blobType = blob.type.toLowerCase();
  const fileName = (blob as File).name?.toLowerCase() || '';
  
  console.log('[VideoService] Detecting extension from type:', blobType, 'filename:', fileName);
  
  if (blobType.includes('mp4') || fileName.endsWith('.mp4')) {
    extension = 'mp4';
  } else if (blobType.includes('quicktime') || blobType.includes('mov') || fileName.endsWith('.mov')) {
    extension = 'mov';
  } else if (blobType.includes('webm') || fileName.endsWith('.webm')) {
    extension = 'webm';
  } else if (fileName.match(/\.(mp4|mov|webm|avi|mkv)$/)) {
    // Extract extension from filename as last resort
    extension = fileName.split('.').pop() || 'webm';
  }
  
  const filePath = `${cleaningId}/${roomId}/${timestamp}.${extension}`;
  const sizeBytes = blob.size;
  
  console.log('[VideoService] Uploading video with type:', blobType, 'filename:', fileName, 'extension:', extension);

  try {
    if (onProgress) {
      onProgress({ loaded: 0, total: sizeBytes, percent: 0 });
    }

    const { error: uploadError } = await supabase.storage
      .from(VIDEO_BUCKET)
      .upload(filePath, blob, {
        contentType: blob.type || 'video/webm',
        upsert: false,
      });

    if (uploadError) {
      return {
        data: null,
        error: createError('Video upload failed. Please try again.', uploadError.message, 'UPLOAD_FAILED'),
      };
    }

    if (onProgress) {
      onProgress({ loaded: sizeBytes, total: sizeBytes, percent: 100 });
    }

    // Insert metadata and get the cleaning video ID
    const { data: insertedVideo, error: insertError } = await supabase
      .from('cleaning_videos')
      .insert([{
        cleaning_id: cleaningId,
        room_id: roomId,
        video_url: filePath,
        duration_seconds: durationSeconds,
        size_bytes: sizeBytes,
      }])
      .select('id')
      .single();

    if (insertError || !insertedVideo) {
      await supabase.storage.from(VIDEO_BUCKET).remove([filePath]);
      return {
        data: null,
        error: createError('Failed to save video metadata', insertError?.message || 'Unknown error', 'METADATA_FAILED'),
      };
    }

    const cleaningVideoId = insertedVideo.id;

    console.log('[VideoService] Upload complete for room:', roomId);
    console.log('[VideoService] Cleaning video ID:', cleaningVideoId);
    
    // Video analysis is temporarily disabled
    // TODO: Re-enable once Gemini API integration is fixed
    const analysisTriggered = false;

    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from(VIDEO_BUCKET)
      .createSignedUrl(filePath, DEFAULT_SIGNED_URL_EXPIRY_SECONDS);

    if (signedUrlError) {
      return {
        data: null,
        error: createError('Failed to generate video URL', signedUrlError.message, 'URL_FAILED'),
      };
    }

    return { 
      data: {
        signedUrl: signedUrlData.signedUrl,
        cleaningVideoId,
        analysisTriggered,
      }, 
      error: null 
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return {
      data: null,
      error: createError('Video upload failed', message, 'UNKNOWN'),
    };
  }
}

export async function getVideoSignedUrl(
  filePath: string,
  expirySeconds: number = DEFAULT_SIGNED_URL_EXPIRY_SECONDS
): Promise<ServiceResult<string>> {
  const { data, error } = await supabase.storage
    .from(VIDEO_BUCKET)
    .createSignedUrl(filePath, expirySeconds);

  if (error) {
    return {
      data: null,
      error: createError('Failed to generate video URL', error.message),
    };
  }

  return { data: data.signedUrl, error: null };
}

export async function getVideosForCleaning(
  cleaningId: string
): Promise<ServiceResult<CleaningVideoRecord[]>> {
  const { data, error } = await supabase
    .from('cleaning_videos')
    .select('*')
    .eq('cleaning_id', cleaningId)
    .order('recorded_at', { ascending: true });

  if (error) {
    return {
      data: null,
      error: createError('Failed to load videos', error.message, error.code),
    };
  }

  return { data, error: null };
}

export async function hasVideoForRoom(
  cleaningId: string,
  roomId: string
): Promise<ServiceResult<boolean>> {
  const { data, error } = await supabase
    .from('cleaning_videos')
    .select('id')
    .eq('cleaning_id', cleaningId)
    .eq('room_id', roomId)
    .maybeSingle();

  if (error) {
    return {
      data: null,
      error: createError('Failed to check video status', error.message, error.code),
    };
  }

  return { data: data !== null, error: null };
}

// ============================================
// Video QA Analysis Functions
// ============================================

/**
 * Get the active host video for a room (to check if analysis is possible)
 * If multiple active exist (bug state), returns highest version
 */
export async function getActiveHostVideo(
  roomId: string
): Promise<ServiceResult<RoomHostVideo | null>> {
  const { data, error } = await supabase
    .from('room_host_videos')
    .select('*')
    .eq('room_id', roomId)
    .eq('active', true)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    return {
      data: null,
      error: createError('Failed to check host video', error.message, error.code),
    };
  }

  return { data, error: null };
}

/**
 * Get host videos for multiple rooms at once (batch operation)
 */
export async function getActiveHostVideosForRooms(
  roomIds: string[]
): Promise<ServiceResult<Record<string, RoomHostVideo>>> {
  if (roomIds.length === 0) {
    return { data: {}, error: null };
  }

  const { data, error } = await supabase
    .from('room_host_videos')
    .select('*')
    .in('room_id', roomIds)
    .eq('active', true)
    .order('version', { ascending: false });

  if (error) {
    return {
      data: null,
      error: createError('Failed to load host videos', error.message, error.code),
    };
  }

  // Create a map of room_id -> host video (highest version wins due to ordering)
  const hostVideoMap: Record<string, RoomHostVideo> = {};
  for (const video of data || []) {
    if (!hostVideoMap[video.room_id]) {
      hostVideoMap[video.room_id] = video;
    }
  }

  return { data: hostVideoMap, error: null };
}

/**
 * Trigger video analysis via Edge Function (fire-and-forget)
 * Returns immediately with 202 status
 */
export async function triggerVideoAnalysis(
  cleaningVideoId: string,
  roomId: string,
  cleaningId: string
): Promise<ServiceResult<void>> {
  try {
    console.log('[VideoService] triggerVideoAnalysis called with:', { cleaningVideoId, roomId, cleaningId });
    
    // Update status to pending before calling Edge Function
    const { error: updateError } = await supabase
      .from('cleaning_videos')
      .update({ analysis_status: 'pending' })
      .eq('id', cleaningVideoId);

    if (updateError) {
      console.error('[VideoService] Failed to update status to pending:', updateError);
      return {
        data: null,
        error: createError('Failed to update analysis status', updateError.message, updateError.code),
      };
    }

    console.log('[VideoService] Status updated to pending, calling Edge Function...');

    // Call Edge Function (fire-and-forget)
    const { data: fnData, error: fnError } = await supabase.functions.invoke('analyze-video', {
      body: {
        cleaning_video_id: cleaningVideoId,
        room_id: roomId,
        cleaning_id: cleaningId,
      },
    });

    console.log('[VideoService] Edge Function response:', { data: fnData, error: fnError });

    if (fnError) {
      console.error('[VideoService] Edge Function error:', fnError);
      // Revert status on failure
      await supabase
        .from('cleaning_videos')
        .update({ analysis_status: 'failed' })
        .eq('id', cleaningVideoId);

      return {
        data: null,
        error: createError('Failed to trigger analysis', fnError.message, 'FUNCTION_ERROR'),
      };
    }

    console.log('[VideoService] Analysis triggered successfully');
    return { data: undefined, error: null };
  } catch (err) {
    console.error('[VideoService] Unexpected error in triggerVideoAnalysis:', err);
    const message = err instanceof Error ? err.message : 'Unknown error';
    return {
      data: null,
      error: createError('Failed to trigger analysis', message, 'UNKNOWN'),
    };
  }
}

/**
 * Retry a failed video analysis
 * Only allowed if current status is 'failed'
 */
export async function retryVideoAnalysis(
  cleaningVideoId: string,
  roomId: string,
  cleaningId: string
): Promise<ServiceResult<void>> {
  // First check current status
  const { data: video, error: checkError } = await supabase
    .from('cleaning_videos')
    .select('analysis_status')
    .eq('id', cleaningVideoId)
    .single();

  if (checkError || !video) {
    return {
      data: null,
      error: createError('Video not found', checkError?.message || 'Unknown error', 'NOT_FOUND'),
    };
  }

  // Only allow retry if failed
  if (video.analysis_status !== 'failed') {
    return {
      data: null,
      error: createError(
        'Cannot retry analysis',
        `Current status is ${video.analysis_status}, retry only allowed for failed analyses`,
        'INVALID_STATUS'
      ),
    };
  }

  // Trigger new analysis
  return triggerVideoAnalysis(cleaningVideoId, roomId, cleaningId);
}

/**
 * Get analysis status for a single video
 */
export async function getVideoAnalysisStatus(
  cleaningVideoId: string
): Promise<ServiceResult<AnalysisStatus>> {
  const { data, error } = await supabase
    .from('cleaning_videos')
    .select('analysis_status')
    .eq('id', cleaningVideoId)
    .single();

  if (error) {
    return {
      data: null,
      error: createError('Failed to get analysis status', error.message, error.code),
    };
  }

  return { data: data.analysis_status as AnalysisStatus, error: null };
}

/**
 * Get all video analyses for a cleaning session (for results modal)
 */
export async function getAnalysesForCleaning(
  cleaningId: string
): Promise<ServiceResult<VideoAnalysis[]>> {
  const { data, error } = await supabase
    .from('video_analyses')
    .select('*')
    .eq('cleaning_id', cleaningId)
    .order('analyzed_at', { ascending: true });

  if (error) {
    return {
      data: null,
      error: createError('Failed to load analyses', error.message, error.code),
    };
  }

  return { data: data || [], error: null };
}

/**
 * Get cleaning videos with their analysis status for a cleaning session
 */
export async function getCleaningVideosWithStatus(
  cleaningId: string
): Promise<ServiceResult<Array<{
  id: string;
  room_id: string;
  analysis_status: AnalysisStatus;
}>>> {
  const { data, error } = await supabase
    .from('cleaning_videos')
    .select('id, room_id, analysis_status')
    .eq('cleaning_id', cleaningId);

  if (error) {
    return {
      data: null,
      error: createError('Failed to load video statuses', error.message, error.code),
    };
  }

  return { data: data || [], error: null };
}

/**
 * Get public URL for host video
 */
export function getHostVideoPublicUrl(videoUrl: string): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  // If videoUrl already contains the bucket name, use it directly
  if (videoUrl.startsWith('host-videos/')) {
    return `${supabaseUrl}/storage/v1/object/public/${videoUrl}`;
  }
  return `${supabaseUrl}/storage/v1/object/public/${HOST_VIDEO_BUCKET}/${videoUrl}`;
}

